/**
 * ตัวเลือก "ผู้ขายประจำ" ต่อโรงงาน — ใช้ร่วมกันในฟอร์มสร้าง แก้ไข และขอเปลี่ยนแปลง
 *
 * โหลดเฉพาะผู้ขาย `ACTIVE` จาก `GET /v1/suppliers?status=ACTIVE` ที่หน้าจอ Supplier
 * Master ใช้อยู่แล้ว (ไม่เพิ่ม endpoint) ค่าว่าง = ยังไม่กำหนด; ล้างค่าได้ด้วยปุ่ม ✕
 *
 * ถ้าโหลดรายการไม่สำเร็จ กล่องยังแสดงรหัสที่ถืออยู่และล้างค่าได้ — ไม่บล็อกการบันทึก
 * ส่วนการตัดสินว่ารหัสใช้ได้หรือไม่เป็นของเซิร์ฟเวอร์ (422 `PREFERRED_SUPPLIER_*`)
 */

import { useEffect, useState } from 'react';

import { SearchableSelect, type SearchableSelectOption } from '../components/SearchableSelect';
import { fetchSuppliers } from '../../lib/suppliers/client';

export interface PreferredSupplierSelectProps {
  readonly apiBaseUrl: string;
  readonly fetchImpl?: typeof fetch;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly ariaLabel: string;
  readonly testId: string;
}

const MAX_PAGES = 10;

export function PreferredSupplierSelect({
  apiBaseUrl,
  fetchImpl,
  value,
  onChange,
  ariaLabel,
  testId,
}: PreferredSupplierSelectProps) {
  const [options, setOptions] = useState<readonly SearchableSelectOption[]>([]);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    void (async () => {
      const collected: SearchableSelectOption[] = [];
      let cursor: string | undefined;
      for (let page = 0; page < MAX_PAGES; page += 1) {
        const result = await fetchSuppliers(
          { status: 'ACTIVE', limit: 200, cursor },
          { apiBaseUrl, fetchImpl, signal: controller.signal },
        );
        if (result.status !== 'ready') {
          break;
        }
        for (const supplier of result.page.items) {
          if (supplier.status === 'ACTIVE') {
            collected.push({
              value: supplier.supplier_code,
              label: `${supplier.supplier_code} — ${supplier.supplier_name}`,
            });
          }
        }
        cursor = result.page.next_cursor ?? undefined;
        if (cursor === undefined) {
          break;
        }
      }
      if (active) {
        setOptions(collected);
      }
    })();
    return () => {
      active = false;
      controller.abort();
    };
  }, [apiBaseUrl, fetchImpl]);

  return (
    <SearchableSelect
      options={options}
      value={value}
      onChange={onChange}
      placeholder="ยังไม่กำหนดผู้ขายประจำ"
      emptyText="ไม่พบผู้ขายที่ใช้งานอยู่"
      clearable
      ariaLabel={ariaLabel}
      testId={testId}
    />
  );
}
